import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { fileUrl } from "../lib/api";
import NotificationBell from "./NotificationBell";
import { BookOpen, Menu, X, LogOut, Shield } from "lucide-react";

const links = [
  { to: "/dashboard", label: "Sanctuary" },
  { to: "/devotionals", label: "Devotionals" },
  { to: "/reading", label: "Word & Wellness" },
  { to: "/prayer", label: "Prayer" },
  { to: "/members", label: "Members", adminOnly: true },
  { to: "/messages", label: "Messages" },
  { to: "/donate", label: "Give" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const isActive = (to) => location.pathname === to || location.pathname.startsWith(to + "/");

  const visibleLinks = links.filter((l) => !l.adminOnly || user?.role === "admin");

  return (
    <header className="sticky top-0 z-50 bg-[#FAF7EE]/85 backdrop-blur-xl border-b border-[#D5CEBF]/60">
      <div className="max-w-7xl mx-auto px-5 md:px-10 h-16 flex items-center justify-between gap-4">
        <Link to="/dashboard" data-testid="nav-logo" className="flex items-center gap-2 shrink-0">
          <BookOpen className="w-6 h-6 text-[#C46B4E]" strokeWidth={1.6} />
          <span className="font-heading text-2xl tracking-tight text-[#2A2621] leading-none">
            The Peace Plot
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {visibleLinks.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-300 ${
                isActive(l.to)
                  ? "text-[#3D5844] bg-[#E7DFCE]"
                  : "text-[#615A50] hover:text-[#3D5844] hover:bg-[#F3EFE4]"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3 shrink-0">
          <NotificationBell />
          {user?.role === "admin" && (
            <Link
              to="/admin"
              data-testid="nav-admin"
              className="flex items-center gap-1 text-sm font-medium text-[#C46B4E] hover:text-[#AC5B40]"
            >
              <Shield className="w-4 h-4" /> Admin
            </Link>
          )}
          <Link to="/profile" data-testid="nav-profile" className="flex items-center gap-2">
            <Avatar user={user} />
          </Link>
          <button
            onClick={handleLogout}
            data-testid="nav-logout"
            className="text-[#615A50] hover:text-[#C46B4E] transition-colors"
            title="Sign out"
          >
            <LogOut className="w-5 h-5" />
          </button>
        </div>

        <button
          className="lg:hidden text-[#2A2621]"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#D5CEBF]/60 bg-[#FAF7EE] px-5 py-3 space-y-1">
          {visibleLinks.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              data-testid={`mnav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`block px-3 py-2.5 rounded-lg text-base font-medium ${
                isActive(l.to) ? "text-[#3D5844] bg-[#E7DFCE]" : "text-[#615A50]"
              }`}
            >
              {l.label}
            </Link>
          ))}
          {user?.role === "admin" && (
            <Link to="/admin" onClick={() => setOpen(false)} className="block px-3 py-2.5 rounded-lg text-base font-medium text-[#C46B4E]">
              Admin
            </Link>
          )}
          <Link to="/profile" onClick={() => setOpen(false)} className="block px-3 py-2.5 rounded-lg text-base font-medium text-[#615A50]">
            My Profile
          </Link>
          <button onClick={handleLogout} className="block w-full text-left px-3 py-2.5 rounded-lg text-base font-medium text-[#C46B4E]">
            Sign out
          </button>
        </div>
      )}
    </header>
  );
}

function Avatar({ user }) {
  const pic = fileUrl(user?.picture);
  if (pic) return <img src={pic} alt={user.name} className="w-9 h-9 rounded-full object-cover border border-[#D5CEBF]" />;
  return (
    <div className="w-9 h-9 rounded-full bg-[#3D5844] text-white flex items-center justify-center text-sm font-semibold">
      {(user?.name || "?").charAt(0).toUpperCase()}
    </div>
  );
}
